/**
 * FunBet.Me Tracking & Redirect System
 * Captures URL params, persists in localStorage, builds dynamic CTA URLs
 * 
 * Tracked params: promo, faff, campaign, channel, source, subid
 * Storage key: fb_track
 * Failsafe: faff defaults to 101 if missing
 */

const STORAGE_KEY = 'fb_track';
const BASE_URL = 'https://funbet.me/en';
const TRACKED_PARAMS = ['promo', 'faff', 'campaign', 'channel', 'source', 'subid'];
const DEFAULT_FAFF = '101';

/**
 * Read stored tracking data from localStorage
 */
function getStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

/**
 * Save tracking data to localStorage
 */
function save(data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // silent fail on storage quota
  }
}

/**
 * Initialize tracking — call once on app load.
 * Reads URL params, merges with stored values (URL wins), persists.
 */
export function initTracking() {
  const stored = getStored();
  const urlParams = new URLSearchParams(window.location.search);
  let updated = false;

  // Merge URL params into stored data (URL overwrites)
  TRACKED_PARAMS.forEach((key) => {
    const val = urlParams.get(key);
    if (val !== null && val !== '') {
      stored[key] = val;
      updated = true;
    }
  });

  // Apply faff default if still missing
  if (!stored.faff) {
    stored.faff = DEFAULT_FAFF;
  }

  // Bonus: store timestamp and landing page on first visit or param change
  if (updated || !stored.timestamp) {
    stored.timestamp = new Date().toISOString();
    stored.landing_page = window.location.href;
  }

  save(stored);

  // Debug log
  if (process.env.NODE_ENV !== 'production' || urlParams.has('debug')) {
    console.log('Tracking data:', JSON.stringify(stored, null, 2));
  }

  return stored;
}

/**
 * Build the full redirect URL with all tracked params appended.
 * @param {string} base — override base URL (default: https://funbet.me/en)
 * @returns {string} full URL with query params
 */
export function buildRedirectUrl(base) {
  const data = getStored();
  const url = new URL(base || BASE_URL);

  TRACKED_PARAMS.forEach((key) => {
    const val = data[key];
    if (val !== undefined && val !== null && val !== '') {
      url.searchParams.set(key, val);
    }
  });

  return url.toString();
}

/**
 * Get current tracking data (read-only snapshot)
 */
export function getTrackingData() {
  return { ...getStored() };
}

/**
 * Handle CTA click — builds URL, fires tracking event, opens in new tab.
 * Use as onClick handler.
 */
export function handleCtaClick(e, trackLabel) {
  const url = buildRedirectUrl();

  // Fire dataLayer event
  try {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'cta_click',
        cta_label: trackLabel || 'funbet_cta',
        redirect_url: url,
        tracking_params: getStored(),
      });
    }
  } catch {
    // silent
  }

  // Persist to /api/analytics/track so admin reports can attribute clicks (incl. A/B variant).
  try {
    const variant = (typeof window !== 'undefined' && window.localStorage)
      ? localStorage.getItem('wc_slide_variant')
      : null;
    // Lazy import to avoid a cycle at module load (unifiedTracking imports from this file? No — but kept lazy for safety).
    import('./unifiedTracking').then(({ trackEvent }) => {
      trackEvent('click', null, trackLabel || 'funbet_cta', {
        metadata: variant ? { variant, destination_url: url } : { destination_url: url },
        allowDuplicate: true,
      });
    }).catch(() => {});
  } catch {
    // silent — tracking failures must never break navigation
  }

  // Debug log
  console.log(`CTA [${trackLabel}] → ${url}`);

  // Open redirect URL
  window.open(url, '_blank', 'noopener,noreferrer');

  // Prevent default if this was an <a> tag
  if (e && e.preventDefault) {
    e.preventDefault();
  }
}
